<template>
  <section class="statistics-card">
    <h3 class="card-title">
      版权数字化治理保护工程业务办理统计
    </h3>

    <div class="channel-tabs">
      <button
        v-for="tab in channelTabs"
        :key="tab.value"
        type="button"
        class="channel-tab"
        :class="{ 'is-active': activeChannel === tab.value }"
        @click="selectChannel(tab.value)"
      >
        {{ tab.label }}
      </button>
    </div>

    <div
      class="business-chart-wrap"
      :class="{ 'is-sub-channel': activeChannel !== 'all' }"
    >
      <div
        ref="chartRef"
        class="business-chart"
      />
      <EmptyPlaceholder
        v-if="showSubChannelEmpty"
        fill
        class="business-chart-empty"
      />
    </div>

    <div
      v-if="activeChannel === 'all'"
      class="business-progress-list"
    >
      <div
        v-for="business in businessItems"
        :key="business.name"
        class="business-progress-row"
      >
        <div class="stacked-track">
          <span
            v-for="(segment, index) in business.segments"
            :key="segment.code"
            class="stacked-segment"
            :style="{
              width: `${segment.percentage}%`,
              backgroundColor: channelItems[index].color
            }"
          />
        </div>
        <div class="business-progress-label">
          <strong>{{ business.name }}</strong>
          <span>{{ business.total }} / {{ business.percentage }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="activeChannel === 'all'"
      class="channel-legend"
    >
      <span
        v-for="item in channelItems"
        :key="item.code"
      >
        <i :style="{ backgroundColor: item.color }" />
        {{ item.shortName }}
      </span>
    </div>
  </section>
</template>

<script setup>
import {
  markRaw,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  shallowRef,
  useTemplateRef
} from 'vue'
import * as echarts from 'echarts'
import {
  getBusinessHandlingOverviewStatistics,
  getBusinessHandlingSubChannelStatistics
} from '@/api/primaryChannelStatistics'
import EmptyPlaceholder from '@/components/EmptyPlaceholder.vue'
import { ChannelIdentityTypeEnum } from '@/enums/ChannelTypeEnum'
import { DIGITAL_FILING_TYPE } from '@/enums/DigitalFilingTypeEnum'

const channelTabs = [
  { label: '总览', value: 'all' },
  {
    label: '直属事业单位及市级文艺家协会',
    value: ChannelIdentityTypeEnum.CITY_ASSOC.code
  },
  { label: '区级文艺家协会', value: ChannelIdentityTypeEnum.DISTRICT_ASSOC.code },
  { label: '文艺两新组织', value: ChannelIdentityTypeEnum.ART_NEW_ORG.code },
  { label: '对口帮扶地区组织', value: ChannelIdentityTypeEnum.SUPPORT_REGION.code }
]

const CHANNEL_DEFINITIONS = [
  {
    code: ChannelIdentityTypeEnum.CITY_ASSOC.code,
    name: '直属事业单位及市级文艺家协会业务数量',
    shortName: '直属事业单位及市级文艺家协会',
    color: '#6546FF'
  },
  {
    code: ChannelIdentityTypeEnum.DISTRICT_ASSOC.code,
    name: '区级文艺家协会业务数量',
    shortName: '区级文艺家协会',
    color: '#126BF2'
  },
  {
    code: ChannelIdentityTypeEnum.ART_NEW_ORG.code,
    name: '文艺两新组织业务数量',
    shortName: '文艺两新组织',
    color: '#FFBF17'
  },
  {
    code: ChannelIdentityTypeEnum.SUPPORT_REGION.code,
    name: '对口帮扶地区组织业务数量',
    shortName: '对口帮扶地区组织',
    color: '#17C789'
  }
]

const BUSINESS_DEFINITIONS = [
  {
    code: DIGITAL_FILING_TYPE.ORIGINAL_DIGITAL_FILING,
    name: '子渠道原创作品备案业务办理数量',
  },
  {
    code: DIGITAL_FILING_TYPE.PRODUCT_RELEASE_FILING,
    name: '子渠道产品发布存证业务办理数量',
  }
]
const DEFAULT_VISIBLE_BAR_COUNT = 10
const BAR_CAROUSEL_INTERVAL = 3000

const channelItems = reactive(buildDefaultChannelItems())
const businessItems = reactive(buildDefaultBusinessItems())
const activeChannel = shallowRef('all')
const showSubChannelEmpty = shallowRef(false)
const chartRef = useTemplateRef('chartRef')
let chart = null
let resizeObserver = null
let resizeFrame = 0
let totalCount = 0
let subChannelItems = []
let requestSequence = 0
let carouselTimer = null
let carouselStartIndex = 0
const subChannelCache = new Map()

function buildDefaultChannelItems() {
  return CHANNEL_DEFINITIONS.map(item => ({ ...item, value: 0 }))
}

function buildDefaultBusinessItems() {
  return BUSINESS_DEFINITIONS.map(item => ({
    ...item,
    total: 0,
    percentage: '0.00%',
    segments: CHANNEL_DEFINITIONS.map(channel => ({
      code: channel.code,
      percentage: 0
    }))
  }))
}

function toNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

function toPercentageNumber(value) {
  return Math.min(
    Math.max(toNumber(String(value ?? '').replace('%', '')), 0),
    100
  )
}

function normalizeOverview(payload = {}) {
  const sourceChannels = Array.isArray(payload.channelItems) ? payload.channelItems : []
  const sourceBusinesses = Array.isArray(payload.businessItems) ? payload.businessItems : []
  totalCount = toNumber(payload.total)

  channelItems.splice(0, channelItems.length, ...CHANNEL_DEFINITIONS.map((definition) => {
    const source = sourceChannels.find(item => item.code === definition.code) || {}
    return {
      ...definition,
      value: toNumber(source.total)
    }
  }))
  businessItems.splice(0, businessItems.length, ...BUSINESS_DEFINITIONS.map((definition) => {
    const source = sourceBusinesses.find(item => item.code === definition.code) || {}
    const sourceSegments = Array.isArray(source.segments) ? source.segments : []
    return {
      ...definition,
      total: toNumber(source.total),
      percentage: source.percentage || '0.00%',
      segments: CHANNEL_DEFINITIONS.map((channel) => {
        const segment = sourceSegments.find(item => item.code === channel.code) || {}
        return {
          code: channel.code,
          percentage: toPercentageNumber(segment.percentage)
        }
      })
    }
  }))
}

function buildOverviewOption() {
  const mobile = (chart?.getWidth() || 0) <= 680
  const center = mobile ? ['50%', '35%'] : ['32%', '50%']
  return {
    color: channelItems.map(item => item.color),
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: '{b}<br/>{c} 件（{d}%）'
    },
    legend: {
      orient: 'vertical',
      left: mobile ? 'center' : '53%',
      bottom: mobile ? 0 : 'center',
      itemWidth: 12,
      itemHeight: 12,
      itemGap: mobile ? 11 : 24,
      icon: 'circle',
      textStyle: {
        color: '#333',
        fontSize: mobile ? 12 : 14,
        rich: {
          name: { width: mobile ? 230 : 300 },
          value: { width: 65, align: 'right', fontWeight: 'bold' }
        }
      },
      formatter: (name) => {
        const item = channelItems.find(current => current.name === name)
        return `{name|${name}}{value|${item?.value || 0} 件}`
      }
    },
    graphic: [{
      type: 'group',
      left: center[0],
      top: center[1],
      bounding: 'raw',
      children: [
        {
          type: 'text',
          y: -12,
          style: {
            text: String(totalCount),
            fill: '#222',
            font: 'bold 32px "Microsoft YaHei"',
            textAlign: 'center'
          }
        },
        {
          type: 'text',
          y: 22,
          style: {
            text: '子渠道业务办理总数',
            fill: '#999',
            font: '13px "Microsoft YaHei"',
            textAlign: 'center'
          }
        }
      ]
    }],
    series: [{
      name: '业务办理统计',
      type: 'pie',
      center,
      radius: mobile ? [72, 110] : [82, 128],
      startAngle: 90,
      stillShowZeroSum: false,
      label: { show: false },
      labelLine: { show: false },
      data: channelItems.map(item => ({
        name: item.name,
        value: item.value,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 1, 1, [
            { offset: 0, color: item.color },
            { offset: 1, color: echarts.color.lift(item.color, 0.25) }
          ])
        }
      }))
    }]
  }
}

function buildSubChannelOption() {
  const selectedChannel = CHANNEL_DEFINITIONS.find(
    item => item.code === activeChannel.value
  ) || CHANNEL_DEFINITIONS[0]
  const names = subChannelItems.map(item => item.name)
  const visibleBarCount = Math.min(DEFAULT_VISIBLE_BAR_COUNT, Math.max(names.length, 1))
  const axisFontSize = names.length > 14 ? 11 : 12
  const availableWidth = Math.max((chart?.getWidth() || 1200) - 98, 300)
  const categoryWidth = availableWidth / visibleBarCount
  const charactersPerLine = Math.max(
    2,
    Math.min(8, Math.floor(categoryWidth / axisFontSize))
  )
  const maxLabelLines = Math.max(
    1,
    ...names.map(name => Math.ceil(Array.from(String(name || '')).length / charactersPerLine))
  )
  const gridBottom = Math.min(190, Math.max(88, 42 + maxLabelLines * 17))

  function formatCompanyName(value) {
    const characters = Array.from(String(value || ''))
    const lines = []
    for (let index = 0; index < characters.length; index += charactersPerLine) {
      lines.push(characters.slice(index, index + charactersPerLine).join(''))
    }
    return lines.join('\n')
  }

  return {
    animationDuration: 500,
    grid: {
      top: 48,
      right: 36,
      bottom: gridBottom,
      left: 62,
      containLabel: false
    },
    tooltip: {
      trigger: 'item',
      confine: true,
      padding: [14, 16],
      backgroundColor: 'rgba(255, 255, 255, 0.96)',
      borderColor: '#ebeef5',
      borderWidth: 1,
      textStyle: { color: '#333', fontSize: 13 },
      extraCssText: 'box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);',
      formatter: (params) => {
        const dataIndex = params?.dataIndex
        const item = subChannelItems[dataIndex] || {}
        const name = echarts.format.encodeHTML(item.name || '-')
        return [
          `<strong>${name}业务办理总数：${item.total || 0} 件</strong>`,
          `原创作品备案：${item.originalTotal || 0} 件`,
          `产品发布存证：${item.productTotal || 0} 件`
        ].join('<br/><br/>')
      }
    },
    dataZoom: names.length > DEFAULT_VISIBLE_BAR_COUNT
      ? [{
        id: 'subChannelCarousel',
        type: 'inside',
        xAxisIndex: 0,
        startValue: carouselStartIndex,
        endValue: carouselStartIndex + DEFAULT_VISIBLE_BAR_COUNT - 1,
        filterMode: 'filter',
        zoomLock: true,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: true,
        moveOnMouseMove: true,
        preventDefaultMouseMove: true
      }]
      : [],
    xAxis: {
      type: 'category',
      data: names,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e6e9ef' } },
      axisLabel: {
        interval: 0,
        color: '#333',
        fontSize: axisFontSize,
        margin: 14,
        lineHeight: 17,
        formatter: formatCompanyName
      }
    },
    yAxis: {
      type: 'value',
      min: 0,
      minInterval: 1,
      splitNumber: 5,
      axisLabel: { color: '#7d8592', fontSize: 12 },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: {
        lineStyle: {
          color: '#dfe3ea',
          type: [4, 4]
        }
      },
      splitArea: { show: false }
    },
    series: [{
      name: selectedChannel.shortName,
      type: 'bar',
      barWidth: 22,
      barMaxWidth: 28,
      data: subChannelItems.map(item => item.total),
      itemStyle: {
        borderRadius: [3, 3, 0, 0],
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: selectedChannel.color },
          { offset: 1, color: echarts.color.lift(selectedChannel.color, 0.35) }
        ])
      },
      label: {
        show: true,
        position: 'top',
        distance: 7,
        color: '#333',
        fontSize: 12,
        formatter: ({ value }) => String(value)
      }
    }]
  }
}

function buildOption() {
  return activeChannel.value === 'all'
    ? buildOverviewOption()
    : buildSubChannelOption()
}

function renderChart() {
  chart?.setOption(buildOption(), { notMerge: true })
}

function stopBarCarousel() {
  if (!carouselTimer) return
  clearInterval(carouselTimer)
  carouselTimer = null
}

function resetBarCarousel() {
  stopBarCarousel()
  carouselStartIndex = 0
}

function startBarCarousel() {
  stopBarCarousel()
  if (subChannelItems.length <= DEFAULT_VISIBLE_BAR_COUNT || activeChannel.value === 'all') {
    return
  }
  carouselTimer = setInterval(() => {
    if (!chart || activeChannel.value === 'all') return
    const maxStartIndex = subChannelItems.length - DEFAULT_VISIBLE_BAR_COUNT
    carouselStartIndex = carouselStartIndex >= maxStartIndex
      ? 0
      : carouselStartIndex + 1
    chart.dispatchAction({
      type: 'dataZoom',
      dataZoomId: 'subChannelCarousel',
      startValue: carouselStartIndex,
      endValue: carouselStartIndex + DEFAULT_VISIBLE_BAR_COUNT - 1
    })
  }, BAR_CAROUSEL_INTERVAL)
}

function normalizeSubChannelItems(payload = {}) {
  const items = Array.isArray(payload.items) ? payload.items : []
  return items.map(item => ({
    id: item.id,
    name: item.name || '-',
    total: toNumber(item.total),
    originalTotal: toNumber(item.originalTotal),
    productTotal: toNumber(item.productTotal)
  }))
}

async function loadOverview() {
  try {
    const response = await getBusinessHandlingOverviewStatistics()
    normalizeOverview(response && response.code === 200 ? response.data : {})
  } catch (error) {
    console.error('查询一级渠道业务办理总览失败:', error)
    normalizeOverview()
  }
  renderChart()
}

async function loadSubChannelStatistics(channelIdentityType) {
  const currentSequence = ++requestSequence
  const cachedItems = subChannelCache.get(channelIdentityType)
  if (cachedItems) {
    subChannelItems = cachedItems
    showSubChannelEmpty.value = cachedItems.length === 0
    renderChart()
    startBarCarousel()
    return
  }
  chart?.showLoading('default', {
    text: '加载中',
    color: '#1769f5',
    maskColor: 'rgba(255, 255, 255, 0.75)'
  })
  try {
    const response = await getBusinessHandlingSubChannelStatistics(channelIdentityType)
    if (currentSequence !== requestSequence || activeChannel.value !== channelIdentityType) return
    const items = normalizeSubChannelItems(
      response && response.code === 200 ? response.data : {}
    )
    subChannelCache.set(channelIdentityType, items)
    subChannelItems = items
    showSubChannelEmpty.value = items.length === 0
  } catch (error) {
    if (currentSequence !== requestSequence || activeChannel.value !== channelIdentityType) return
    console.error('查询子渠道业务办理统计失败:', error)
    subChannelItems = []
    showSubChannelEmpty.value = true
  } finally {
    if (currentSequence === requestSequence) chart?.hideLoading()
  }
  renderChart()
  startBarCarousel()
}

function selectChannel(channelIdentityType) {
  if (activeChannel.value === channelIdentityType) return
  resetBarCarousel()
  activeChannel.value = channelIdentityType
  if (channelIdentityType === 'all') {
    requestSequence++
    showSubChannelEmpty.value = false
    chart?.hideLoading()
    renderChart()
    return
  }
  subChannelItems = []
  showSubChannelEmpty.value = false
  renderChart()
  loadSubChannelStatistics(channelIdentityType)
}

onMounted(async () => {
  await nextTick()
  if (!chartRef.value) return
  chart = markRaw(echarts.init(chartRef.value))
  renderChart()
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame)
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0
        chart?.resize()
        renderChart()
      })
    })
    resizeObserver.observe(chartRef.value)
  }
  loadOverview()
})

onBeforeUnmount(() => {
  if (resizeFrame) cancelAnimationFrame(resizeFrame)
  stopBarCarousel()
  resizeObserver?.disconnect()
  chart?.dispose()
})
</script>

<style lang="scss" scoped>
.statistics-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 24px 28px 30px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
}

.card-title {
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.4;
}

.channel-tabs {
  display: inline-flex;
  max-width: 100%;
  margin-top: 22px;
  overflow-x: auto;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.channel-tab {
  min-width: 108px;
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-right: 1px solid #dcdfe6;
  background: #fff;
  color: #333;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
  cursor: pointer;

  &:first-child {
    min-width: 72px;
  }

  &:last-child {
    border-right: 0;
  }

  &.is-active {
    background: #1769f5;
    color: #fff;
  }
}

.business-chart-wrap {
  position: relative;
  width: 100%;
  height: 380px;
  margin-top: 14px;

  &.is-sub-channel {
    height: 430px;
  }
}

.business-chart {
  width: 100%;
  height: 100%;
}

.business-chart-empty {
  position: absolute;
  z-index: 1;
  inset: 0;
  background: #fff;
}

.business-progress-list {
  width: min(1040px, 94%);
  margin: 0 auto;
}

.business-progress-row {
  display: grid;
  margin-bottom: 18px;
  grid-template-columns: minmax(360px, 1fr) minmax(340px, auto);
  align-items: center;
  gap: 28px;
}

.stacked-track {
  display: flex;
  height: 32px;
  overflow: hidden;
  background: #eef0f3;
}

.stacked-segment {
  display: block;
  height: 100%;
}

.business-progress-label {
  display: flex;
  align-items: center;
  gap: 28px;
  color: #333;
  font-size: 13px;
  white-space: nowrap;

  strong {
    font-weight: 600;
  }
}

.channel-legend {
  display: flex;
  margin-top: 30px;
  align-items: center;
  justify-content: center;
  gap: 34px;
  color: #333;
  font-size: 12px;

  span {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
  }

  i {
    width: 11px;
    height: 11px;
    margin-right: 8px;
    border-radius: 50%;
  }
}

@media (max-width: 900px) {
  .business-progress-list {
    width: 100%;
  }

  .business-progress-row {
    grid-template-columns: minmax(220px, 1fr) minmax(280px, auto);
  }

  .channel-legend {
    flex-wrap: wrap;
    gap: 16px 28px;
  }
}

@media (max-width: 768px) {
  .statistics-card {
    padding: 16px;
  }

  .card-title {
    font-size: 16px;
  }

  .channel-tabs {
    display: flex;
    width: 100%;
  }

  .business-chart-wrap,
  .business-chart-wrap.is-sub-channel {
    height: 450px;
  }

  .business-progress-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .business-progress-label {
    justify-content: space-between;
  }
}

@media (max-width: 480px) {
  .statistics-card {
    padding: 12px;
  }

  .card-title {
    font-size: 15px;
  }

  .channel-tab {
    min-width: 108px;
    height: 34px;
    padding: 0 10px;
    font-size: 11px;
  }

  .business-chart-wrap,
  .business-chart-wrap.is-sub-channel {
    height: 420px;
  }

  .business-progress-label {
    gap: 10px;
    font-size: 11px;
  }

  .channel-legend {
    justify-content: flex-start;
  }
}
</style>
